import {
  HSDoseRound,
  HSFacility, HSPackedPatientDay, HSPackedPatientPrnMedication,
  HSPatient, HSTestResult, PatientMovementWithData,
} from 'server-openapi';
import {Entry, IStorage} from '../core/storage/Contract';
import { SyncStreamAPI } from './api';
import {IFacilityGroupSyncService} from './SyncCenter';
import { SyncUtils } from './utils/SyncUtils';
import { Logger } from '../core/logger/logger';
import { DateUtils } from '../core/utils/dateUtils';
import { startOfDay, subDays } from 'date-fns';
import {MemoryCache} from "../core/storage/MemoryCache";
import {parseInt} from "lodash-es";
import {mergePackedPatientDays} from "./utils/PackedPatientDayUtils";

const logger = new Logger('SyncPatientMovements');

export class SyncPatientMovements implements IFacilityGroupSyncService {
  get name(): string {
    return 'SyncPatientMovements';
  }

  private async isStale(p: PatientMovementWithData): Promise<boolean> {
    return !!((!p.active && p.lastUpdated && p.lastUpdated < DateUtils.fromDate(subDays(startOfDay(new Date()), 14))));
  }

  private async isFacilityGroup(p: PatientMovementWithData, facilityGroupId: string): Promise<boolean> {
    const facilityGroup = await this.facilitiesStore.get(p.movedFrom!.facilityId!.toString());
    return facilityGroup?.facilityGroupId?.toString() == facilityGroupId;
  }

  // Initialise the patient store to an empty store.  It will get written properly when the load method is called.
  constructor(
    private api: SyncStreamAPI,
    private storage: MemoryCache<PatientMovementWithData>,
    private facilitiesStore: IStorage<HSFacility>,
    private patientsStore: IStorage<HSPatient>,
    private testResultsStore: IStorage<HSTestResult>,
    private packedPatientDayStore: IStorage<HSPackedPatientDay>,
    private packedPatientPrnMedicationStore: IStorage<HSPackedPatientPrnMedication>,
    private roundStore: IStorage<HSDoseRound>,
    private latestChangeNumbers: IStorage<number | undefined>,
    private epochStore: MemoryCache<string>,
  ) {}

  patientMovements: PatientMovementWithData[] = [];
  async load(facilityGroupId: string): Promise<void> {
    await this.storage.reset(async (p) => {
          return await this.isFacilityGroup(p, facilityGroupId);
    });
  }

  //eslint-disable-next-line sonarjs/cognitive-complexity
  async syncDown(facilityGroupId?: string) {
    const facilitiesToSync = await SyncUtils.getFacilitiesForGroup(facilityGroupId, this.facilitiesStore);
    const changeNumber = await SyncUtils.getChangeNumberForFacilities(
        facilitiesToSync.map((x) => x.hsId!),
        this.latestChangeNumbers,
    );

    // fetch patient movements for all facilities within facility group
    const patientMovementData = await this.syncFacilityDown(
      facilityGroupId!, // non-null assertion on facility group id
      facilitiesToSync.map((facility) => facility.hsId!),
      changeNumber);

    for (let patientMovement of patientMovementData) {
      const existingPatientMovement = await this.storage.get(patientMovement.hsId!.toString()) as PatientMovementWithData;
      if (!existingPatientMovement ||  existingPatientMovement.changeNumber! < patientMovement.changeNumber!) {
        logger.info(`Processing movement ${patientMovement.hsId} of patient ${patientMovement.patientId} from ward ${patientMovement.movedFrom?.facilityName} to ${patientMovement.movedTo?.facilityName}`);
        const sourceFacility = await this.facilitiesStore.get(patientMovement.movedFrom!.facilityId!.toString()) as HSFacility;
        const destinationFacility = await this.facilitiesStore.get(patientMovement.movedTo!.facilityId!.toString()) as HSFacility;
        let patient = await this.patientsStore.get(patientMovement.patientId!.toString()) as HSPatient;
        if (!destinationFacility && patient) {
          // This is not a facility we know about - just remove it.
          logger.info("Patient movement to unmanaged facility group ", facilityGroupId);
          await this.patientsStore.delete(patientMovement.patientId!.toString()); 
        }
        else {
          // This is a movement in from elsewhere.  Pull in all of the relevant dose data.

          logger.info("Incoming patient movement from facility ", sourceFacility?.hsId);
          await this.handleTestResults(patientMovement);
          await this.handlePackedPatientDays(patientMovement);
          await this.handlePackedPatientPrnMedications(patientMovement);
          await this.handleRounds(patientMovement);
        }
      }
    }


    await this.storage.setMany(
      patientMovementData
        .map((patientMovement) => ({
          key: this.storage.get_key!(patientMovement),
          value: patientMovement,
        }))
    );

    await SyncUtils.setChangeNumberForFacilities(
        facilitiesToSync.map((facility) => facility.hsId!),
        this.latestChangeNumbers,
        patientMovementData
    );
  }

  private async handleRounds(patientMovement: PatientMovementWithData) {
    const doseRoundUpdates: HSDoseRound[] = [];
    for (const round of patientMovement.doseRounds ?? []) {
      var existing = await this.roundStore.get(round.clinicalSystemId?.toString() ?? "");
      if (!existing && round.hsId) {
        doseRoundUpdates.push(round);
      } 
      else if (existing && round.hsId && round.hsId == existing.hsId) {
        existing = {...existing};
        let modified = false;
        // Merge the rounds.
        // Need to merge rounds because two different patients from the same round could be moved at the same time - in which case they will
        // both be in the rounds.  But the round information that is sent across only includes the information in the round for a single patient so need to merge them.
        if (round.administeredDoses) {
          existing.administeredDoses ??= [];
          for (var idx = 0; idx < (round.administeredDoses ?? []).length; idx++) {
            const ad = round.administeredDoses[idx];            
            // If it is not in the existing, then add it.
            var matchingDose = existing.administeredDoses.find(x => x.hsId == ad.hsId);
            if (matchingDose) {
              if ((ad.version ?? -1) > (matchingDose.version ?? -1)) {
                // The version in the patient movement is > than the existing version so replace it.
                var matchIdx = existing.administeredDoses.indexOf(matchingDose)
                existing.administeredDoses[matchIdx] = ad;
                modified = true;
              }
            }
            else {
              // No existing dose.  Add it.
              existing.administeredDoses.push(ad)
              modified = true;
            }
          }
        }
        if (round.doseRoundSegments) {
          existing.doseRoundSegments ??= [];
          for (const ad of round.doseRoundSegments ?? []) {
            // If it is not in the existing, then add it.
            if (!existing.doseRoundSegments.some(x => x.hsId == ad.hsId)) {
              existing.doseRoundSegments.push(ad);
              modified = true;
            }
          }
        }
        if (modified) {
          doseRoundUpdates.push(existing);
        }
      }
    }
    if (doseRoundUpdates.length > 0) {
      logger.info(`Updating ${doseRoundUpdates.length} dose rounds`);
      await this.roundStore.setMany(
          doseRoundUpdates.map((rnd) => ({
            key: this.roundStore.get_key!(rnd),
            value: rnd,
          })),
      );
    }
  }

  private async handlePackedPatientPrnMedications(patientMovement: PatientMovementWithData) {
    const pppmUpdates: HSPackedPatientDay[] = [];
    for (const pppm of patientMovement.packedPatientPrnMedications ?? []) {
      const existing = await this.packedPatientPrnMedicationStore.get(pppm.hsId?.toString() ?? "");
      if (pppm.hsId && (!existing?.version || existing.version < (pppm.version ?? 0))) {
        pppmUpdates.push(pppm);
      }
    }
    if (pppmUpdates.length > 0) {
      logger.info(`Updating ${pppmUpdates.length} packed patient prn medications`);
      await this.packedPatientPrnMedicationStore.setMany(
          pppmUpdates.map((pppm) => ({
            key: this.packedPatientPrnMedicationStore.get_key!(pppm),
            value: pppm,
          })),
      );
    }
  }

  private async handlePackedPatientDays(patientMovement: PatientMovementWithData) {
    const ppdUpdates: HSPackedPatientDay[] = [];
    for (const ppd of patientMovement.packedPatientDays ?? []) {
      // Need to merge the ppd with the data from the patient movement.
      let existing:HSPackedPatientDay | undefined;

      (await this.packedPatientDayStore.all()).forEach(x => {
        if (x.facilityId === ppd.facilityId && x.patientId === ppd.patientId && x.packDate === ppd.packDate) {
          existing = {...x};
        }
      });
      if (existing) {
        const modified = mergePackedPatientDays(existing, ppd);
        if (modified) {
          // There was at least 1 change to the ppd, so update it.
          ppdUpdates.push(modified);
        }
      }
    }
    if (ppdUpdates.length > 0) {
      logger.info(`Updating ${ppdUpdates.length} packed patient days`);
      await this.packedPatientDayStore.setMany(
          ppdUpdates.map((ppd) => ({
            key: this.packedPatientDayStore.get_key!(ppd),
            value: ppd,
          })),
      );
    }
  }

  private async handleTestResults(patientMovement: PatientMovementWithData) {
    const testResultUpdates: HSTestResult[] = [];
    for (const testResult of patientMovement.testResults ?? []) {
      const existing = await this.testResultsStore.get(testResult.hsId?.toString() ?? "");
      if (testResult.hsId && (!existing?.version || existing.version < (testResult.version ?? 0))) {
        testResultUpdates.push(testResult);
      }
    }
    if (testResultUpdates.length > 0) {
      logger.info(`Updating ${testResultUpdates.length} test results`);
      await this.testResultsStore.setMany(
          testResultUpdates.map((tr) => ({
            key: this.testResultsStore.get_key!(tr),
            value: tr,
          })),
      );
    }
  }

  private async syncFacilityDown(
    facilityGroupId: string,
    facilityIds: number[],
    changeNumber: number,
  ): Promise<PatientMovementWithData[]> {
    const pageSize = 200;
    //changeNumber = isFinite(changeNumber) ? changeNumber : 0;
    const patientMovements = await this.api.patientMovements.patientMovementsListPatientMovements(
      parseInt(facilityGroupId),
      changeNumber,
      pageSize,
    );

    this.patientMovements = patientMovements.data;

    if (patientMovements.data.length === pageSize) {
      return [
        ...patientMovements.data,
        ...(await this.syncFacilityDown(facilityGroupId, facilityIds, SyncUtils.getLatestChangeNumber(patientMovements.data)!)),
      ];
    }
    return patientMovements.data;
  }

  async clear() {
    await this.storage.clear();
    await this.latestChangeNumbers.clear();
  }

  isAllowed(canUserAccessMedication: boolean): boolean {
    // Only if you can view a round.
    return canUserAccessMedication;
  }
  async archive(): Promise<string[]> {
    const keysToDelete: string[] = [];
    for (let [k, v] of (await this.storage.all()).entries()) {
      if (await this.isStale(v)) {
        keysToDelete.push(k)
      }
    }
    await this.storage.deleteMany(keysToDelete);
    return keysToDelete;
  }

  setEncryptionVersion(version: number): void {
    this.storage.compressOnSave = (version > 1);
  }
  async rewrite(): Promise<void> {
    const entries: Entry<PatientMovementWithData>[] = [...(await this.storage.all())].map((keyValueArray) => {
      return {
        key: keyValueArray[0],
        value: keyValueArray[1]
      };
    });
    return await this.storage.setMany(entries);
  }
}
